// src/pages/organiser/PostEventPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { createEvent, buildRRule } from '../../lib/db';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAIFill } from '../../lib/useAIFill';
import { storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import toast from 'react-hot-toast';

const CATEGORIES = [
  { value: 'music',     label: 'Music',     emoji: '🎵' },
  { value: 'sport_gaa', label: 'Sport - GAA',           emoji: '🏐' },
  { value: 'sport',     label: 'Sport - Other',         emoji: '🏃' },
  { value: 'kids',      label: 'Kids and Family',       emoji: '🧒' },
  { value: 'health',    label: 'Health and Wellbeing',  emoji: '🏥' },
  { value: 'farming',   label: 'Farming and Equine',    emoji: '🌾' },
  { value: 'food',      label: 'Food and Drink',        emoji: '🍽' },
  { value: 'education', label: 'Education',             emoji: '📚' },
  { value: 'social',    label: 'Social',                emoji: '🎉' },
  { value: 'community', label: 'Community', emoji: '🤝' },
  { value: 'markets',   label: 'Markets',   emoji: '🛒' },
  { value: 'arts',      label: 'Arts',      emoji: '🎭' },
];

const REPEATS = [
  { value: 'once',        label: 'One-off',     emoji: '1️⃣' },
  { value: 'weekly',      label: 'Weekly',      emoji: '🔁' },
  { value: 'fortnightly', label: 'Fortnightly', emoji: '📅' },
  { value: 'monthly',     label: 'Monthly',     emoji: '🗓️' },
];

const EMPTY = {
  title: '', date: '', time: '', venue: '',
  description: '', category: 'music',
  repeat: 'once', eventType: 'free', price: '',
  organiserPhone: '', organiserEmail: '', bookingUrl: '',
};

const PostEventPage = () => {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { fill, loading: aiLoading, error: aiError } = useAIFill();

  // 'choose' | 'form' | 'ai'
  const [mode, setMode] = useState('choose');

  const [form, setForm] = useState(EMPTY);
  const [aiInput, setAiInput] = useState('');
  const [aiStep, setAiStep] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Pre-fill organiser contact details from saved profile
  useEffect(() => {
    if (profile) {
      setForm(f => ({
        ...f,
        organiserPhone: profile.organiserPhone || '',
        organiserEmail: profile.organiserEmail || '',
        bookingUrl:     profile.bookingUrl     || '',
        venue:          profile.savedVenue     || '',
      }));
    }
  }, [profile]);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: null }));
  };

  // ── AI Fill ──────────────────────────────────────────
  const handleAIFill = async () => {
    if (!aiInput.trim()) return;
    setAiStep('loading');
    const result = await fill(aiInput);
    if (result) {
      setForm(f => ({
        ...f,
        title:       result.title       || f.title,
        date:        result.date        || f.date,
        time:        result.time        || f.time,
        venue:       result.venue       || f.venue,
        description: result.description || f.description,
        category:    result.category    || f.category,
        repeat:      result.repeat      || f.repeat,
        eventType:   result.eventType   || f.eventType,
        price:       result.price != null ? String(result.price) : f.price,
        // Only override the logged-in account's own saved contact details
        // if the pasted text actually named a different organiser/contact —
        // e.g. a community page pasting a caption written by someone else.
        // Otherwise keep whatever the form already had.
        organiserPhone: result.organiserPhone || f.organiserPhone,
        organiserEmail: result.organiserEmail || f.organiserEmail,
      }));
      if (result.organiserPhone || result.organiserEmail) {
        toast('Found different contact details in that text — double-check they\'re correct before publishing', { icon: 'ℹ️', duration: 5000 });
      }
      setAiStep('done');
      toast.success('Form filled! Check everything looks right.');
      setMode('form');
    } else {
      setAiStep(null);
      toast.error(aiError || 'Could not read that — try describing the event in plain English');
    }
  };

  // ── Validation ───────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title.trim())       e.title = 'Event name is required';
    if (!form.date.trim())        e.date = 'Date is required';
    if (!form.time.trim())        e.time = 'Time is required';
    if (!form.venue.trim())       e.venue = 'Venue is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.eventType === 'paid' && !form.price) e.price = 'Price is required for paid events';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ───────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      // date = yyyy-mm-dd (native picker), time = HH:mm (24hr, native picker)
      const [year, month, day] = form.date.split('-');
      const [hours, minutes] = form.time.split(':').map(Number);

      const eventDate = new Date(
        parseInt(year), parseInt(month) - 1, parseInt(day), hours || 0, minutes || 0
      );

      let imageUrl = null;
      if (imageFile) {
        const imgRef = ref(storage, `events/${user.uid}/${Date.now()}`);
        await uploadBytes(imgRef, imageFile);
        imageUrl = await getDownloadURL(imgRef);
      }

      const eventData = {
        title:         form.title.trim(),
        date:          eventDate,
        nextDate:      eventDate,
        time:          form.time,
        venue:         form.venue.trim(),
        description:   form.description.trim(),
        category:      form.category,
        repeat:        form.repeat,
        rrule:         buildRRule(eventDate, form.repeat),
        eventType:     form.eventType,
        eventCharge:   form.eventType === 'paid' ? `€${form.price}` : 'Free',
        organiserPhone: form.organiserPhone || null,
        organiserEmail: form.organiserEmail || null,
        bookingUrl:    form.bookingUrl || null,
        imageUrl,
        townId:        profile?.defaultTownId || localStorage.getItem('gather-town-id'),
        organiserId:   user.uid,
        organiserName: profile?.displayName || user.email,
      };

      await createEvent(eventData);

      // Save organiser contact details to profile for next time
      if (user?.uid) {
        await updateDoc(doc(db, 'users', user.uid), {
          organiserPhone: form.organiserPhone || null,
          organiserEmail: form.organiserEmail || null,
          bookingUrl:     form.bookingUrl     || null,
          savedVenue:     form.venue.trim()   || null,
        });
        await refreshProfile();
      }
      toast.success('Event published! 🎉');
      navigate('/organiser');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── SCREEN: Choose mode ──────────────────────────────
  if (mode === 'choose') {
    return (
      <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
        <button style={s.back} onClick={() => navigate(-1)}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>Back</span>
        </button>
        <div style={{ marginBottom: 36 }}>
          <div style={s.h2}>Post an event</div>
          <div style={s.sub}>How would you like to add it?</div>
        </div>

        {/* Manual form option */}
        <button style={s.choiceCard} onClick={() => setMode('form')}>
          <span style={s.choiceEmoji}>📋</span>
          <div>
            <div style={s.choiceTitle}>Fill in the form</div>
            <div style={s.choiceSub}>Takes about 60 seconds. Enter your event details step by step.</div>
          </div>
          <span style={s.choiceArrow}>›</span>
        </button>

        {/* AI option */}
        <button style={{ ...s.choiceCard, ...s.choiceCardAI }} onClick={() => setMode('ai')}>
          <span style={s.choiceEmoji}>✨</span>
          <div>
            <div style={{ ...s.choiceTitle, color: 'white' }}>AI shortcut</div>
            <div style={{ ...s.choiceSub, color: 'rgba(255,255,255,0.75)' }}>
              Paste a Facebook link or describe your event. AI fills the whole form instantly.
            </div>
          </div>
          <span style={{ ...s.choiceArrow, color: 'rgba(255,255,255,0.6)' }}>›</span>
        </button>
      </div>
    );
  }

  // ── SCREEN: AI mode ──────────────────────────────────
  if (mode === 'ai') {
    return (
      <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>
        <button style={s.back} onClick={() => setMode('choose')}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>Back</span>
        </button>
        <div style={{ marginBottom: 24 }}>
          <div style={s.h2}>AI shortcut</div>
          <div style={s.sub}>Paste a Facebook link or describe your event in plain English.</div>
        </div>

        <div style={s.aiBanner}>
          <div style={s.aiRow}>
            <input
              style={s.aiInput}
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              placeholder="e.g. facebook.com/events/... or 'trad session every Friday at 8pm in The White House'"
            />
          </div>
          <button
            style={{ ...s.aiGo, opacity: aiLoading ? 0.7 : 1, marginTop: 10, width: '100%' }}
            onClick={handleAIFill}
            disabled={aiLoading}
          >
            {aiLoading ? 'Reading your event...' : 'Fill form ✨'}
          </button>

          {aiStep === 'loading' && (
            <div style={s.aiSteps}>
              {['Finding details', 'Extracting date & time', 'Getting location', 'Filling form'].map((step, i) => (
                <div key={i} style={s.aiStepRow}>
                  <div style={s.aiDot} />
                  <span style={s.aiStepText}>{step}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <button style={s.switchLink} onClick={() => setMode('form')}>
          Prefer to fill it in manually →
        </button>
      </div>
    );
  }

  // ── SCREEN: Form ─────────────────────────────────────
  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', paddingBottom: 110 }}>

      <div style={s.header}>
        <button style={s.back} onClick={() => setMode('choose')}>
          <div style={s.backCircle}>←</div>
          <span style={s.backLbl}>Back</span>
        </button>
        <div style={s.hdr}>
          <div style={s.h2}>Post an event</div>
          <div style={s.sub}>Takes about 60 seconds</div>
        </div>
      </div>

      <div style={s.form}>

        {/* Event name */}
        <Field label="Event name *" error={errors.title}>
          <input
            style={{ ...s.input, borderColor: errors.title ? 'var(--error)' : '#C8D9C9' }}
            value={form.title}
            onChange={e => set('title', e.target.value)}
            
          />
        </Field>

        {/* Description — directly after name */}
        <Field label="Description *" error={errors.description}>
          <textarea
            style={{ ...s.input, height: 90, resize: 'none', borderColor: errors.description ? 'var(--error)' : '#C8D9C9' }}
            value={form.description}
            onChange={e => set('description', e.target.value)}
            
          />
        </Field>

        {/* Date & Time */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Date *" error={errors.date}>
            <input
              type="date"
              style={{ ...s.input, borderColor: errors.date ? 'var(--error)' : '#C8D9C9', colorScheme: 'light' }}
              value={form.date}
              onChange={e => set('date', e.target.value)}
            />
          </Field>
          <Field label="Time *" error={errors.time}>
            <input
              type="time"
              step="900"
              style={{ ...s.input, borderColor: errors.time ? 'var(--error)' : '#C8D9C9', colorScheme: 'light' }}
              value={form.time}
              onChange={e => set('time', e.target.value)}
            />
          </Field>
        </div>

        {/* Venue */}
        <Field label="Location / Venue *" error={errors.venue}>
          <input
            style={{ ...s.input, borderColor: errors.venue ? 'var(--error)' : '#C8D9C9' }}
            value={form.venue}
            onChange={e => set('venue', e.target.value)}
            
          />
        </Field>

        {/* Category */}
        <Field label="Category">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.value}
                style={{
                  ...s.tileBtn,
                  border: form.category === cat.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.category === cat.value ? 'var(--sage-pale)' : 'var(--white)',
                  color: form.category === cat.value ? 'var(--sage-deep)' : '#2C4A2E',
                  fontWeight: form.category === cat.value ? 800 : 600,
                }}
                onClick={() => set('category', cat.value)}
              >
                <span style={{ fontSize: 20, display: 'block', marginBottom: 4 }}>{cat.emoji}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </Field>

        {/* Event Type — Free or Paid */}
        <Field label="Is this event free or paid?">
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { value: 'free', label: 'Free', emoji: '🎉', sub: 'No charge to attend' },
              { value: 'paid', label: 'Paid', emoji: '💶', sub: 'Set a price' },
            ].map(et => (
              <button
                key={et.value}
                style={{
                  ...s.etypeBtn,
                  flex: 1,
                  border: form.eventType === et.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.eventType === et.value ? 'var(--sage-pale)' : 'var(--white)',
                }}
                onClick={() => set('eventType', et.value)}
              >
                <span style={{ fontSize: 24, display: 'block', marginBottom: 6 }}>{et.emoji}</span>
                <div style={{ ...s.etTitle, color: form.eventType === et.value ? 'var(--sage-deep)' : '#2C4A2E' }}>
                  {et.label}
                </div>
                <div style={s.etSub}>{et.sub}</div>
              </button>
            ))}
          </div>
        </Field>

        {/* Price — only if paid */}
        {form.eventType === 'paid' && (
          <Field label="Price (€) *" error={errors.price}>
            <input
              style={{ ...s.input, borderColor: errors.price ? 'var(--error)' : '#C8D9C9' }}
              type="number"
              value={form.price}
              onChange={e => set('price', e.target.value)}
              
            />
          </Field>
        )}

        {/* Optional contact fields */}
        <Field label="Your phone number (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="tel"
            value={form.organiserPhone}
            onChange={e => set('organiserPhone', e.target.value)}
            
          />
        </Field>

        <Field label="Your email (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="email"
            value={form.organiserEmail}
            onChange={e => set('organiserEmail', e.target.value)}
            
          />
        </Field>

        <Field label="Booking link (optional)">
          <input
            style={{ ...s.input, borderColor: '#C8D9C9' }}
            type="url"
            value={form.bookingUrl}
            onChange={e => set('bookingUrl', e.target.value)}
            
          />
        </Field>

        {/* Image */}
        <Field label="Event image (optional)">
          <label style={s.imageUpload}>
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => setImageFile(e.target.files[0])}
            />
            {imageFile
              ? <span style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>✓ {imageFile.name}</span>
              : <span style={{ color: '#2C4A2E', fontWeight: 600 }}>📷 Tap to add a photo</span>
            }
          </label>
        </Field>

        {/* Repeat — moved to bottom */}
        <Field label="Does this event repeat?">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {REPEATS.map(r => (
              <button
                key={r.value}
                style={{
                  ...s.tileBtn,
                  border: form.repeat === r.value ? '2px solid var(--sage-deep)' : '1.5px solid #C8D9C9',
                  background: form.repeat === r.value ? 'var(--sage-pale)' : 'var(--white)',
                  color: form.repeat === r.value ? 'var(--sage-deep)' : '#2C4A2E',
                  fontWeight: form.repeat === r.value ? 800 : 600,
                }}
                onClick={() => set('repeat', r.value)}
              >
                <span style={{ fontSize: 18, display: 'block', marginBottom: 4 }}>{r.emoji}</span>
                {r.label}
              </button>
            ))}
          </div>
        </Field>

      </div>

      {/* Submit bar */}
      <div style={s.submitBar}>
        <button
          style={{ ...s.submitBtn, opacity: submitting ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Publishing...' : 'Publish event →'}
        </button>
      </div>
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: '#2C4A2E' }}>
      {label}
    </span>
    {children}
    {error && <span role="alert" style={{ fontSize: 12, color: 'var(--error)', fontWeight: 600 }}>{error}</span>}
  </label>
);

const s = {
  header: { padding: '52px 20px 0' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12 },
  backCircle: {
    width: 34, height: 34, background: 'var(--white)', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, boxShadow: 'var(--shadow-sm)', color: '#2C4A2E',
  },
  backLbl: { fontSize: 13, color: '#2C4A2E', fontWeight: 600 },
  hdr: { marginBottom: 16 },
  h2: { fontSize: 26, fontWeight: 800, color: '#1A2E1A', letterSpacing: '-0.3px', marginBottom: 3 },
  sub: { fontSize: 14, color: '#4A6A4A', fontWeight: 500 },

  form: { padding: '16px 18px 0', display: 'flex', flexDirection: 'column', gap: 16 },

  input: {
    width: '100%', padding: '12px 14px',
    borderRadius: 12, border: '1.5px solid #C8D9C9',
    fontSize: 15, fontFamily: 'Nunito, sans-serif',
    color: '#1A2E1A', fontWeight: 500,
    background: 'var(--white)', boxSizing: 'border-box', outline: 'none',
  },

  tileBtn: {
    background: 'var(--white)', border: '1.5px solid #C8D9C9',
    borderRadius: 13, padding: '11px 6px',
    textAlign: 'center', cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
    fontFamily: 'Nunito, sans-serif', transition: 'all 0.15s',
  },

  etypeBtn: {
    background: 'var(--white)', border: '1.5px solid #C8D9C9',
    borderRadius: 14, padding: '16px 12px',
    textAlign: 'center', cursor: 'pointer',
    fontFamily: 'Nunito, sans-serif', transition: 'all 0.15s',
  },
  etTitle: { fontSize: 15, fontWeight: 800, marginBottom: 3 },
  etSub: { fontSize: 11, color: '#4A6A4A', fontWeight: 500 },

  imageUpload: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--white)', border: '1.5px dashed #C8D9C9',
    borderRadius: 14, padding: '20px', cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },

  // Choice screen
  choiceCard: {
    width: '100%', background: 'var(--white)',
    border: '1.5px solid #C8D9C9', borderRadius: 18,
    padding: '20px 16px', marginBottom: 14,
    display: 'flex', alignItems: 'center', gap: 14,
    cursor: 'pointer', textAlign: 'left',
    fontFamily: 'Nunito, sans-serif',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  choiceCardAI: {
    background: '#2C4A2E', border: '1.5px solid #2C4A2E',
  },
  choiceEmoji: { fontSize: 32, flexShrink: 0 },
  choiceTitle: { fontSize: 16, fontWeight: 800, color: '#1A2E1A', marginBottom: 4 },
  choiceSub: { fontSize: 13, color: '#4A6A4A', fontWeight: 500, lineHeight: 1.5 },
  choiceArrow: { fontSize: 22, color: '#C8D9C9', marginLeft: 'auto', flexShrink: 0 },

  // AI screen
  aiBanner: {
    background: '#2C4A2E', borderRadius: 18, padding: 18, marginBottom: 16,
  },
  aiRow: { display: 'flex', gap: 8 },
  aiInput: {
    flex: 1, background: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: 11, padding: '12px 14px',
    fontSize: 14, color: 'white',
    fontFamily: 'Nunito, sans-serif', fontWeight: 500, outline: 'none',
  },
  aiGo: {
    background: 'var(--sage-deep)', border: 'none', borderRadius: 12,
    padding: '14px', fontSize: 15, fontWeight: 700,
    color: 'white', fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer',
  },
  aiSteps: { marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  aiStepRow: { display: 'flex', alignItems: 'center', gap: 10 },
  aiDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--sage-deep)', flexShrink: 0 },
  aiStepText: { fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 500 },
  switchLink: {
    background: 'none', border: 'none', color: '#4A6A4A',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'Nunito, sans-serif', padding: 0, marginTop: 8,
    textDecoration: 'underline',
  },

  submitBar: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 430,
    padding: '13px 18px 26px',
    background: 'var(--white)', borderTop: '1.5px solid var(--border)', zIndex: 100,
  },
  submitBtn: {
    width: '100%', background: 'var(--sage-deep)', color: 'white',
    border: 'none', padding: 17, borderRadius: 16,
    fontSize: 16, fontWeight: 700, fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer',
  },
};

export default PostEventPage;
